import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Plane, Clock } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './FlightTimeCalculatorPage/components/CalculationForm';
import ResultsOverview from './FlightTimeCalculatorPage/components/ResultsOverview';
import FlightSchedule from './FlightTimeCalculatorPage/components/FlightSchedule';
import FlightInfoSidebar from './FlightTimeCalculatorPage/components/FlightInfoSidebar';
import PopularRoutesSidebar from './FlightTimeCalculatorPage/components/PopularRoutesSidebar';
import TipsSidebar from './FlightTimeCalculatorPage/components/TipsSidebar';
import InfoContent from './FlightTimeCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  departureCity: 'Москва',
  arrivalCity: 'Санкт-Петербург',
  flightType: 'direct', // 'direct' или 'connecting'
  flightClass: 'economy', // 'economy', 'comfort', 'business', 'first'
  departureDate: new Date().toISOString().split('T')[0],
  returnDate: '',
  isRoundTrip: false,
  includeLayover: false,
  layoverCity: 'Казань',
  layoverDuration: '2', // В часах
  aircraftType: 'medium', // 'small', 'medium', 'large', 'regional'
  includeDelays: false,
  delayFactor: 'low', // 'low', 'medium', 'high'
  includeTimezones: true,
};

// Определение типов самолетов
export const aircraftTypes = [
  { id: 'regional', name: 'Региональный самолет', avgSpeed: 550, examples: 'Sukhoi Superjet 100, Як-40' },
  { id: 'small', name: 'Малый самолет', avgSpeed: 800, examples: 'Ту-134, Ан-148' },
  { id: 'medium', name: 'Средний самолет', avgSpeed: 850, examples: 'Ту-204, МС-21, Airbus A320, Boeing 737' },
  { id: 'large', name: 'Дальнемагистральный', avgSpeed: 900, examples: 'Ил-96, Boeing 777, Airbus A330' }
];

// Определение классов обслуживания
export const flightClasses = [
  { id: 'economy', name: 'Эконом-класс' },
  { id: 'comfort', name: 'Комфорт-класс' },
  { id: 'business', name: 'Бизнес-класс' },
  { id: 'first', name: 'Первый класс' }
];

// Определение факторов задержки
export const delayFactors = [
  { id: 'low', name: 'Низкий', value: 0.05 }, // 5% к времени полета
  { id: 'medium', name: 'Средний', value: 0.1 }, // 10% к времени полета
  { id: 'high', name: 'Высокий', value: 0.2 } // 20% к времени полета
];

/**
 * FlightTimeCalculatorPage Component - Калькулятор времени полета с учетом различных факторов.
 */
const FlightTimeCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('flight-time-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор времени полета | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте точное время полета между городами России и мира с учетом различных факторов: типа самолета, пересадок, часовых поясов, и типичных задержек.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [flightRoutes, setFlightRoutes] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedFlights, setSavedFlights] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных маршрутов из localStorage ---
  useEffect(() => {
    const savedFlightsData = localStorage.getItem('savedFlights');
    if (savedFlightsData) {
      try {
        setSavedFlights(JSON.parse(savedFlightsData));
      } catch (e) {
        console.error("Error loading saved flights:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'direct':
          newState.flightType = 'direct';
          newState.includeLayover = false;
          break;
        case 'connecting':
          newState.flightType = 'connecting';
          newState.includeLayover = true;
          break;
        case 'international':
          newState.departureCity = 'Москва';
          newState.arrivalCity = 'Стамбул';
          newState.includeTimezones = true;
          break;
        case 'domestic':
          newState.departureCity = 'Москва';
          newState.arrivalCity = 'Владивосток';
          newState.includeTimezones = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setFlightRoutes([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение маршрута в localStorage
  const saveFlight = (name) => {
    if (!results) return;

    const newSavedFlight = {
      id: Date.now().toString(),
      name: name || `${formData.departureCity} - ${formData.arrivalCity}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedFlights = [...savedFlights, newSavedFlight];
    setSavedFlights(updatedSavedFlights);
    localStorage.setItem('savedFlights', JSON.stringify(updatedSavedFlights));

    return newSavedFlight;
  };

  // Удаление сохраненного маршрута
  const deleteSavedFlight = (id) => {
    const updatedSavedFlights = savedFlights.filter(flight => flight.id !== id);
    setSavedFlights(updatedSavedFlights);
    localStorage.setItem('savedFlights', JSON.stringify(updatedSavedFlights));
  };

  // Загрузка сохраненного маршрута
  const loadSavedFlight = (id) => {
    const flightToLoad = savedFlights.find(flight => flight.id === id);
    if (flightToLoad) {
      setFormData(flightToLoad.formData);
      setResults(flightToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Plane className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор времени полета'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              flightRoutes={flightRoutes}
              setFlightRoutes={setFlightRoutes}
              aircraftTypes={aircraftTypes}
              flightClasses={flightClasses}
              delayFactors={delayFactors}
              saveFlight={saveFlight}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                
                {flightRoutes.length > 0 && (
                  <FlightSchedule 
                    flightRoutes={flightRoutes} 
                    results={results}
                  />
                )}
              </>
            )}

            {/* --- Информационный контент о полетах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Информация о полетах --- */}
            <FlightInfoSidebar 
              formData={formData}
              results={results}
            />

            {/* --- Популярные маршруты --- */}
            <PopularRoutesSidebar 
              savedFlights={savedFlights}
              deleteSavedFlight={deleteSavedFlight}
              loadSavedFlight={loadSavedFlight}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Flight Schedule Table */
        .flight-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .flight-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .flight-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .flight-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .flight-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .flight-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .flight-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .flight-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .flight-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default FlightTimeCalculatorPage;